import { getEntityScope } from './entity-scope-read';
import { tokenClaims } from './entity-claim';
import { scopeRootOfClaims, subjectOfClaims } from '../util/jwt';
import { isSwitching, noteTriggerDropped } from './switch-in-progress';
import { seedScopeRoot } from './entity-scope-persistence';
import { hasUnsavedWork, onUnsavedWorkCleared } from './unsaved-work';
import { __resetStampCheckForTests, stampUnstampedScope } from './scope-stamp-check';
import {
  __resetMoveTrackerForTests,
  forgetConfirmedMove,
  isMoveConfirmed,
  noteAgreeingToken,
} from './scope-move-tracker';
import { getStore } from '../store/get-store';
import { showAlert } from '../slice/AlertSlice';
import { rootMovedSince, UNREACHABLE_ROOT, userChangedSince } from '../slice/entityScopeSlice';
import { Variants } from '../types/common';

/**
 * Cross-tab session check — DV-3006, absorbed by DV-3013 spec §3.6 / D10,
 * narrowed by DV-3573 and widened again by DV-3421.
 *
 * <p>ONE token per user, shared by every tab through localStorage. Since
 * DV-3573 an in-tree switch does not touch that token, so tabs switching
 * inside one tree are independent. Two things still reach every tab through
 * it:
 *
 * <ul>
 *   <li>A CROSS-TREE switch in one tab re-anchors the token (DV-3421). The
 *       other tabs' selections are then outside what it may reach, so they
 *       reload — `scope-moved` below — once the move is confirmed, no page
 *       holds unsaved work, and the switching tab has written the seed they
 *       will reload into.
 *   <li>The token being REPLACED — a different user logging in from another
 *       tab.
 * </ul>
 *
 * <p>For the second, this tab then holds the previous user's
 * selection under the new user's token. It must not re-point itself at the
 * new user's account (that would hand one person's screen to another), and it
 * must not end the session either: the session in storage is the NEW user's,
 * and logging it out would log them out of every tab. So it reloads, which
 * drops this tab's in-memory state and boots fresh from the new user's.
 *
 * <p>The token's `sub` is the authority; every trigger is only a hint. This
 * function re-reads it each time, so no individual trigger has to be reliable.
 */

export type ReconcileResult =
  /** The token still belongs to the user this tab's scope was written under. */
  | { kind: 'in-sync' }
  /** No token — a logout elsewhere. The logout arm owns that. */
  | { kind: 'no-claim' }
  /** This tab has deliberately un-chosen its account. */
  | { kind: 'unscoped' }
  /**
   * A different person now holds the shared token. This tab has started a
   * reload into that person's session; nothing else was touched.
   */
  | { kind: 'user-changed' }
  /**
   * This tab's scope has no root stamp (persisted before DV-3421 shipped), so
   * whether its token still reaches it is unknown. The backend is being asked
   * about the selection, and the scope is stamped with the answer (review
   * round 6, item A). It re-checks when the answer lands.
   */
  | { kind: 'checking-stamp' }
  /**
   * The shared token's scope root disagrees with this tab's stamp, and the
   * stored token alone cannot settle it — issued in the same second as the
   * newest one that agreed, or this tab has never seen one agree. THIS tab is
   * running one fresh refresh to find out (review rounds 5–6, review of
   * 0a17c730 items 5 and 8). It re-checks when the refresh settles.
   */
  | { kind: 'verifying-move' }
  /**
   * The move is confirmed, but a page holds unsaved work. Nothing is reloaded
   * until it is discarded — it cannot be saved: requests carry this tab's
   * selection, which the guard refuses (review rounds 5–6).
   */
  | { kind: 'move-deferred' }
  /**
   * The move is confirmed, and the shared seed does not name the new root yet:
   * the tab that switched writes it a moment after its token lands. Waits for
   * that write, or {@link SEED_GRACE_MS}, so the reload adopts the new account
   * rather than landing unscoped (review round 5, item 5).
   */
  | { kind: 'awaiting-seed' }
  /**
   * The token was re-anchored to another tree and the move is confirmed, so
   * this tab's selection is no longer inside what the token may reach
   * (DV-3421). Same local teardown as `user-changed`, for the same reason.
   */
  | { kind: 'scope-moved' }
  /** This tab is mid-switch; its own disagreement is expected and transient. */
  | { kind: 'switching' };

/**
 * How long a confirmed move waits for the seed before reloading anyway. A
 * switch in another tab writes it within one entity-document fetch; a root
 * that moved with no switch behind it — an admin added the user to a second
 * company and a refresh picked it up — never gets one, and that tab reloads
 * onto the picker after this.
 */
export const SEED_GRACE_MS = 5_000;

let awaitingSeed: { root: string; since: number } | null = null;
let seedTimer: ReturnType<typeof setTimeout> | null = null;
let stopWaitingForSave: (() => void) | null = null;
let alertedRoot: string | null = null;

export const reconcileScope = (): ReconcileResult => {
  // Decoded ONCE per trigger; everything below reads these (review of
  // 0a17c730, item 9).
  const claims = tokenClaims();
  const sub = subjectOfClaims(claims);
  // endSession() removes the token, so a cross-tab logout lands here rather
  // than in any of the branches below. App.tsx's logout arm redirects; this
  // must not also act on a session that is going away.
  if (!sub) return { kind: 'no-claim' };

  const current = getEntityScope();
  // An unscoped tab is not displaying one user's account under another's
  // token, so there is nothing to undo — and acting would be wrong:
  // '/select-organization' is `entity: 'none'`, which PrivateRoute reads as
  // the assertion "no org is selected", so a scoped tab sitting on the picker
  // is bounced to '/'.
  if (current.status !== 'scoped') return { kind: 'unscoped' };

  // The header's predicate, so the two cannot disagree about this tab.
  //
  // DV-3573 review: a LOCAL teardown, deliberately not `endSession()`. That
  // would revoke the session in storage — the new user's — and clear shared
  // storage, logging them out of every tab seconds after they signed in. A
  // full-page load drops this tab's in-memory scope and lets the bootstrap
  // resolve the new user's from scratch. It converges because
  // `readPersistedScope()` reads a scope stamped with another user as
  // unscoped, so the next load cannot land back here.
  if (userChangedSince(sub, current)) {
    window.location.assign('/');
    return { kind: 'user-changed' };
  }

  // DV-3421 review round 2, issue 3. THIS tab is mid cross-tree switch: it
  // has written a re-anchored token but not yet the scope that goes with it,
  // so its own stamp disagrees with its own token. Reloading would abandon the
  // switch half applied. Only THIS check stands down for it — a logout or a
  // different user (above) is acted on mid-switch too (review round 4,
  // issue 4); the flag is per tab, so other tabs are unaffected.
  if (isSwitching()) {
    noteTriggerDropped();
    return { kind: 'switching' };
  }

  // DV-3421 review finding 4. Without this the other tabs sit on an account
  // the re-anchored token cannot reach, denied on every request with nothing
  // on screen to say so — the same silent state this ticket exists to remove,
  // arriving by a different route. Converges for the same reason: a scope
  // stamped with another root reads back as unscoped.
  const root = scopeRootOfClaims(claims);
  const iat = typeof claims?.iat === 'number' ? claims.iat : null;

  // Review round 6, item A. A scope persisted before the stamp existed has no
  // root to compare, and treating that as "no move" left deploy-day sessions
  // blind to one for good. Ask once per selection and root; the answer becomes
  // the stamp, and the checks below take over.
  if (current.rootAtWrite === null) {
    if (root === null || current.entityId === null) return { kind: 'in-sync' };
    // Settled locally when it can be, which stamps the scope: look again.
    if (stampUnstampedScope(current, root, reconcileScope)) return reconcileScope();
    return { kind: 'checking-stamp' };
  }

  if (!rootMovedSince(root, current)) {
    noteAgreeingToken(iat);
    forgetMove();
    return { kind: 'in-sync' };
  }
  // `rootMovedSince` is false for a null root.
  const movedTo = root as string;
  // The backend has already said this selection is unreachable (item A): no
  // token needs second-guessing, and no switching tab will write a seed for it.
  const unreachable = current.rootAtWrite === UNREACHABLE_ROOT;

  // Review rounds 5–6, item 6 / F. The token writer refuses to replace a newer
  // token for the same user (`processTokenData`), so a disagreeing token in
  // storage is normally the newest — believed with no refresh, so N open tabs
  // do not each refresh on one `storage` event. Two cases still get ONE
  // verifying refresh: a token issued no later than the newest one that
  // agreed with this tab (the writer lets an equal issue second through), and
  // a tab that has never seen one agree, which has nothing to compare against.
  if (!unreachable && !isMoveConfirmed(movedTo, iat, reconcileScope)) {
    return { kind: 'verifying-move' };
  }

  // Review round 5, item 5. A document load never reaches the router's dirty
  // blocker, so ask first. Deferring is the safe direction: this tab keeps
  // sending its selection, and the guard refuses it — a visible failure, not
  // a write landing in the other account.
  if (hasUnsavedWork()) {
    deferUntilDiscarded(movedTo, unreachable);
    return { kind: 'move-deferred' };
  }

  // Reloading before the switching tab has written the seed would discard the
  // old-root seed and land unscoped. Its write fires `storage` here, which runs
  // this again. Not for an unreachable stamp: nothing is switching, so no seed
  // is coming (review of 0a17c730, item 4).
  if (!unreachable && seedScopeRoot() !== movedTo && !seedGraceElapsed(movedTo)) {
    return { kind: 'awaiting-seed' };
  }

  forgetMove();
  window.location.assign('/');
  return { kind: 'scope-moved' };
};

const deferUntilDiscarded = (root: string, unreachable: boolean): void => {
  if (!stopWaitingForSave) {
    stopWaitingForSave = onUnsavedWorkCleared(() => {
      stopWaitingForSave?.();
      stopWaitingForSave = null;
      reconcileScope();
    });
  }
  if (alertedRoot === root) return;
  alertedRoot = root;
  try {
    getStore().dispatch(
      showAlert({
        type: Variants.WARNING,
        // Not "save": a save from this tab carries a selection the token no
        // longer reaches, and is refused (review round 6, item D).
        // An unreachable stamp has no "other tab" behind it (item 4).
        message: unreachable
          ? 'This organization is no longer available to your session, so changes here can no longer be saved. Discard them and this tab will reload.'
          : 'Your organization was changed in another tab, so changes here can no longer be saved. Discard them and this tab will reload.',
      }),
    );
  } catch {
    // No store yet (pre-render): nothing is mounted, so there is no work to lose.
  }
};

const seedGraceElapsed = (root: string): boolean => {
  const now = Date.now();
  if (awaitingSeed?.root !== root) awaitingSeed = { root, since: now };
  const left = awaitingSeed.since + SEED_GRACE_MS - now;
  if (left <= 0) return true;
  if (seedTimer === null) {
    seedTimer = setTimeout(() => {
      seedTimer = null;
      reconcileScope();
    }, left);
  }
  return false;
};

/** Drop every trace of a move this tab was tracking. */
const forgetMove = (): void => {
  forgetConfirmedMove();
  awaitingSeed = null;
  alertedRoot = null;
  if (seedTimer !== null) {
    clearTimeout(seedTimer);
    seedTimer = null;
  }
  stopWaitingForSave?.();
  stopWaitingForSave = null;
};

/** Test seam: forget any move being tracked, including a pending verification. */
export const __resetReconciliationForTests = (): void => {
  forgetMove();
  __resetMoveTrackerForTests();
  __resetStampCheckForTests();
};
